/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Check, Loader2 } from 'lucide-react';
import { JupiterToken, getJupiterTokenList } from '../lib/solana/jupiter';

interface TokenSelectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (token: any) => void;
    selectedTokenAddress?: string;
    chain: 'SOL' | 'SUI';
}

// Mock Sui Tokens for the list since there isn't a single standard open API like Jupiter yet
const MOCK_SUI_TOKENS = [
    { name: 'Sui', symbol: 'SUI', address: '0x2::sui::SUI', decimals: 9, logoURI: 'https://cryptologos.cc/logos/sui-sui-logo.png' },
    { name: 'HOT NETWORK', symbol: 'HOT', address: '0xHotDAO_SUI', decimals: 9, logoURI: '/assets/hot-token.svg' },
    { name: 'USD Coin', symbol: 'USDC', address: '0x5d4b302506645c37ff133b971b3f4534b9501d32', decimals: 6, logoURI: 'https://cryptologos.cc/logos/usd-coin-usdc-logo.png' },
    { name: 'Tether USD', symbol: 'USDT', address: '0xc060006111016b8a020ad5b33834984142041ca6', decimals: 6, logoURI: 'https://cryptologos.cc/logos/tether-usdt-logo.png' },
    { name: 'Cetus', symbol: 'CETUS', address: '0x06864a6f921804860930db6ddbe2e16acdf8504495ea7481637a1c8b9a8fe54b::cetus::CETUS', decimals: 9, logoURI: 'https://archive.cetus.zone/assets/cetus_logo.png' },
    { name: 'Turbos', symbol: 'TURBOS', address: '0x...turbos', decimals: 9, logoURI: 'https://turbos.finance/logo.png' },
    { name: 'DeepBook', symbol: 'DEEP', address: '0x...deep', decimals: 9, logoURI: 'https://sui.io/favicon.ico' },
    { name: 'FUD the Pug', symbol: 'FUD', address: '0x...fud', decimals: 5, logoURI: 'https://bafkreic6t75...ipfs.nftstorage.link' },
    { name: 'Blub', symbol: 'BLUB', address: '0x...blub', decimals: 9, logoURI: 'https://placehold.co/100x100/4da2ff/ffffff?text=BLUB' }
];

export const TokenSelectModal: React.FC<TokenSelectModalProps> = ({ 
    isOpen, onClose, onSelect, selectedTokenAddress, chain 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [tokens, setTokens] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            if (chain === 'SOL') {
                getJupiterTokenList().then(list => {
                    setTokens(list);
                    setLoading(false);
                }).catch(() => setLoading(false));
            } else {
                // Load Sui Tokens
                // In a real app, fetch from a SUI aggregator API
                setTimeout(() => {
                    setTokens(MOCK_SUI_TOKENS);
                    setLoading(false);
                }, 300);
            }
        }
    }, [isOpen, chain]);

    const filteredTokens = useMemo(() => {
        if (!searchTerm) return tokens.slice(0, 100); // Show top 100 by default
        const lowerSearch = searchTerm.toLowerCase();
        return tokens.filter(t => 
            t.symbol.toLowerCase().includes(lowerSearch) || 
            t.name.toLowerCase().includes(lowerSearch) ||
            t.address === searchTerm
        ).slice(0, 50);
    }, [tokens, searchTerm]);

    if (!isOpen) return null;

    // Helper to handle image errors with Chain-Specific branding
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, symbol: string) => {
        const target = e.target as HTMLImageElement;
        // Use Blue/Cyan for Sui, generic Grey/Gold for Solana fallback
        const bgColor = chain === 'SUI' ? '4da2ff' : '1a1a1a'; 
        target.src = `https://placehold.co/100x100/${bgColor}/ffffff?text=${symbol[0]}`;
    };

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[600px] border border-stone-200"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-white">
                    <h3 className="font-serif text-xl font-bold text-stone-900 flex items-center gap-2">
                        Select Token
                        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide border ${chain === 'SUI' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-purple-50 text-purple-600 border-purple-100'}`}>
                            {chain === 'SUI' ? 'Sui' : 'Solana'}
                        </span>
                    </h3>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-stone-100 rounded-lg transition-colors text-stone-400 hover:text-stone-900"
                        title="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-stone-100 bg-stone-50">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                        <input 
                            type="text" 
                            placeholder="Search name or paste address"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-3 bg-white border border-stone-200 rounded-xl focus:ring-2 focus:ring-nobel-gold focus:border-transparent outline-none text-sm shadow-sm"
                            autoFocus
                        />
                    </div>
                    {/* Common Bases */}
                    <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar pb-1">
                        {tokens.slice(0, 4).map(t => (
                            <button 
                                key={t.address}
                                onClick={() => { onSelect(t); onClose(); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-200 rounded-full hover:border-stone-400 transition-colors shrink-0 shadow-sm"
                            >
                                <img 
                                    src={t.logoURI} 
                                    className="w-4 h-4 rounded-full" 
                                    onError={(e) => handleImageError(e, t.symbol)}
                                />
                                <span className="text-xs font-bold text-stone-700">{t.symbol}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar bg-white p-2">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full text-stone-400 gap-2">
                            <Loader2 className="animate-spin text-nobel-gold" size={24} />
                            <span className="text-xs font-bold">Loading {chain} Token List...</span>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredTokens.map((token) => {
                                const isSelected = token.address === selectedTokenAddress;
                                return (
                                    <button
                                        key={token.address}
                                        onClick={() => { onSelect(token); onClose(); }}
                                        className={`w-full flex items-center justify-between p-3 rounded-xl hover:bg-stone-50 transition-colors ${isSelected ? 'bg-stone-50 border border-stone-100' : 'border border-transparent'}`}
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className={`w-10 h-10 rounded-full overflow-hidden border border-stone-200 shrink-0 ${chain === 'SUI' ? 'bg-blue-50' : 'bg-stone-100'}`}>
                                                <img 
                                                    src={token.logoURI} 
                                                    className="w-full h-full object-cover" 
                                                    alt={token.symbol}
                                                    onError={(e) => handleImageError(e, token.symbol)}
                                                />
                                            </div>
                                            <div className="text-left overflow-hidden">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-stone-900">{token.symbol}</span>
                                                    {chain === 'SUI' && <span className="text-[9px] bg-blue-100 text-blue-600 px-1 rounded font-bold">MOVE</span>}
                                                </div>
                                                <div className="text-xs text-stone-500 truncate max-w-[200px]">{token.name}</div>
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                                                <Check size={14} />
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                            {filteredTokens.length === 0 && (
                                <div className="p-8 text-center text-stone-400 text-sm">
                                    No tokens found.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};